/**
 * BenchmarkStatsHeader — the analysis page's header strip, console
 * style: an open <dl> row between hairlines instead of stat cards, so
 * nothing truncates. Sections and difficulty list every entry in full.
 */

import { DIFFICULTY_TONES } from '@/components/evals/analysis'
import { formatScore } from '@/components/evals/format'
import type { DifficultyCounts } from '@/components/benchmarks/benchmarkAnalysis'

interface BenchmarkStatsHeaderProps {
  taskCount: number
  meanScore: number | null
  models: string[]
  sections: { section: string; score: number }[]
  difficulty: DifficultyCounts
}

function StatBlock({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="min-w-28">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  )
}

export function BenchmarkStatsHeader({
  taskCount,
  meanScore,
  models,
  sections,
  difficulty,
}: BenchmarkStatsHeaderProps): React.JSX.Element {
  const difficultyRows = [
    { label: 'Easy', count: difficulty.easy, tone: DIFFICULTY_TONES.easy },
    { label: 'Medium', count: difficulty.medium, tone: DIFFICULTY_TONES.medium },
    { label: 'Hard', count: difficulty.hard, tone: DIFFICULTY_TONES.hard },
    ...(difficulty.unscored > 0
      ? [{ label: 'Unscored', count: difficulty.unscored, tone: DIFFICULTY_TONES.unscored }]
      : []),
  ]

  return (
    <dl className="flex flex-wrap gap-x-12 gap-y-6 border-y border-border py-5">
      <StatBlock label="Tasks">
        <span className="font-mono text-2xl tabular-nums text-foreground">{taskCount}</span>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {models.length} {models.length === 1 ? 'model' : 'models'}
        </p>
      </StatBlock>

      <StatBlock label="Mean score">
        <span className="font-mono text-2xl tabular-nums text-foreground">
          {formatScore(meanScore)}
        </span>
        <p className="mt-0.5 text-xs text-muted-foreground">Overall</p>
      </StatBlock>

      {sections.length > 0 && (
        <StatBlock label="By section">
          <div className="space-y-0.5 text-sm">
            {sections.map((entry) => (
              <p key={entry.section} className="text-foreground">
                <span className="font-mono tabular-nums">{formatScore(entry.score)}</span>{' '}
                <span className="text-muted-foreground">{entry.section}</span>
              </p>
            ))}
          </div>
        </StatBlock>
      )}

      <StatBlock label="Difficulty">
        <div className="space-y-0.5 text-sm">
          {difficultyRows.map((row) => (
            <p key={row.label}>
              <span className="font-mono tabular-nums" style={{ color: row.tone }}>
                {row.count}
              </span>{' '}
              <span className="text-muted-foreground">{row.label}</span>
            </p>
          ))}
        </div>
      </StatBlock>
    </dl>
  )
}
